import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface MenteeProgress {
  id: string;
  name: string;
  meetingsCompleted: number;
  meetingsScheduled: number;
  lastMeetingDate: string;
  status: 'on-track' | 'at-risk' | 'needs-attention';
  completionRate: number;
}

export function MentorIntelligence() {
  const { user } = useAuth();
  const [mentees, setMentees] = useState<MenteeProgress[]>([]);
  const [stats, setStats] = useState({
    totalMentees: 0,
    averageCompletion: 0,
    upcomingMeetings: 0,
    atRiskMentees: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMentorData();
  }, [user]);

  const loadMentorData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get mentor assignments
      const { data: assignments } = await supabase
        .from('mentor_assignments')
        .select('mentee_id, profiles!mentor_assignments_mentee_id_fkey(id, first_name, last_name)')
        .eq('mentor_id', user.id)
        .eq('status', 'active');

      if (!assignments || assignments.length === 0) {
        setMentees([]);
        return;
      }

      const menteeIds = assignments.map(a => a.mentee_id);

      // Get meetings data
      const { data: meetings } = await supabase
        .from('meetings')
        .select('observee_id, status, scheduled_date')
        .eq('observer_id', user.id)
        .in('observee_id', menteeIds);

      // Process mentee data
      const menteeProgress = assignments.map(assignment => {
        const profile = assignment.profiles as any;
        const menteeMeetings = meetings?.filter(m => m.observee_id === assignment.mentee_id) || [];
        
        const completed = menteeMeetings.filter(m => m.status === 'completed').length;
        const scheduled = menteeMeetings.filter(m => 
          m.status === 'scheduled' && new Date(m.scheduled_date) > new Date()
        ).length;
        
        const lastMeeting = menteeMeetings
          .filter(m => m.status === 'completed')
          .sort((a, b) => new Date(b.scheduled_date).getTime() - new Date(a.scheduled_date).getTime())[0];

        const daysSinceLastMeeting = lastMeeting 
          ? Math.floor((Date.now() - new Date(lastMeeting.scheduled_date).getTime()) / (1000 * 60 * 60 * 24))
          : 999;

        // Determine status
        let status: 'on-track' | 'at-risk' | 'needs-attention' = 'on-track';
        if (daysSinceLastMeeting > 14) status = 'at-risk';
        else if (daysSinceLastMeeting > 7) status = 'needs-attention';

        const completionRate = completed > 0 ? (completed / (completed + scheduled || 1)) * 100 : 0;

        return {
          id: assignment.mentee_id,
          name: `${profile?.first_name} ${profile?.last_name}`,
          meetingsCompleted: completed,
          meetingsScheduled: scheduled,
          lastMeetingDate: lastMeeting ? lastMeeting.scheduled_date : 'Never',
          status,
          completionRate: Math.round(completionRate)
        };
      });

      // Calculate stats
      const totalMentees = menteeProgress.length;
      const avgCompletion = menteeProgress.reduce((sum, m) => sum + m.completionRate, 0) / totalMentees;
      const upcoming = menteeProgress.reduce((sum, m) => sum + m.meetingsScheduled, 0);
      const atRisk = menteeProgress.filter(m => m.status === 'at-risk').length;

      setMentees(menteeProgress);
      setStats({
        totalMentees,
        averageCompletion: Math.round(avgCompletion),
        upcomingMeetings: upcoming,
        atRiskMentees: atRisk
      });
    } catch (error) {
      console.error('Error loading mentor data:', error);
      toast.error('Failed to load mentor intelligence');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading mentor intelligence...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Users className="h-6 w-6" />
        Mentor Intelligence Dashboard
      </h2>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Mentees</p>
                <p className="text-3xl font-bold">{stats.totalMentees}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Completion</p>
                <p className="text-3xl font-bold">{stats.averageCompletion}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Upcoming Meetings</p>
                <p className="text-3xl font-bold">{stats.upcomingMeetings}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">At-Risk</p>
                <p className="text-3xl font-bold">{stats.atRiskMentees}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mentee Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Mentee Progress Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mentees.map((mentee) => (
              <div key={mentee.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{mentee.name}</h3>
                    <Badge 
                      variant={
                        mentee.status === 'on-track' ? 'default' : 
                        mentee.status === 'needs-attention' ? 'secondary' : 
                        'destructive'
                      }
                    >
                      {mentee.status === 'on-track' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {mentee.status === 'needs-attention' && <Clock className="h-3 w-3 mr-1" />}
                      {mentee.status === 'at-risk' && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {mentee.status.replace('-', ' ')}
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {mentee.completionRate}% complete
                  </span>
                </div>

                <Progress value={mentee.completionRate} />

                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{mentee.meetingsCompleted} meetings completed</span>
                  <span>{mentee.meetingsScheduled} scheduled</span>
                  <span>
                    Last met: {mentee.lastMeetingDate !== 'Never' 
                      ? format(new Date(mentee.lastMeetingDate), 'PP')
                      : 'Never'}
                  </span>
                </div>
              </div>
            ))}

            {mentees.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No active mentees assigned
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* At-Risk Mentees Alert */}
      {stats.atRiskMentees > 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
          <CardHeader>
            <CardTitle className="text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Attention Needed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {stats.atRiskMentees} mentee{stats.atRiskMentees > 1 ? 's' : ''} haven't had a meeting in over 14 days. 
              Consider scheduling check-ins to ensure they stay on track.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
